/**
 * AlloyPriceEvolutionCard — evolução do preço de cada liga de alumínio.
 *
 * Consome o histórico de versões (`/config/alloys/{id}/versions`) e desenha
 * as séries Base MB, Prémio de Compra, Prémio de Venda e o Total de Compra
 * (Base MB + Prémio de Compra) ao longo das datas de vigência.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip as RTooltip, Legend,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { configAPI } from '@/services/api';
import { t } from '@/i18n/translations';

const SERIES = [
  { key: 'base', labelKey: 'base_mb', color: '#3B82F6' },
  { key: 'buy', labelKey: 'premium_buy', color: '#10B981' },
  { key: 'sell', labelKey: 'premium_sell', color: '#F59E0B' },
  { key: 'totalBuy', labelKey: 'alloy_total_buy', color: '#6366F1' },
];

export default function AlloyPriceEvolutionCard({ lang }) {
  const [alloys, setAlloys] = useState([]);
  const [selected, setSelected] = useState('');
  const [versions, setVersions] = useState([]);

  useEffect(() => {
    let active = true;
    configAPI.getAlloys()
      .then((res) => {
        if (!active) return;
        const list = (res.data || []).filter((a) => a.active);
        setAlloys(list);
        if (list.length) setSelected(String(list[0].id));
      })
      .catch(() => setAlloys([]));
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (!selected) {
      setVersions([]);
      return undefined;
    }
    let active = true;
    configAPI.getAlloyVersions(selected)
      .then((res) => { if (active) setVersions(res.data || []); })
      .catch(() => { if (active) setVersions([]); });
    return () => { active = false; };
  }, [selected]);

  const chartData = useMemo(
    () => (versions || []).map((v) => ({
      label: v.effective_date
        ? String(v.effective_date).slice(0, 10)
        : `v${v.version_number}`,
      base: v.base_mb_eur_per_t || 0,
      buy: v.premium_eur_per_t || 0,
      sell: v.premium_sell_eur_per_t || 0,
      totalBuy: (v.base_mb_eur_per_t || 0) + (v.premium_eur_per_t || 0),
    })),
    [versions],
  );

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-4 space-y-0">
        <CardTitle>{t('chart_alloy_price_evolution', lang)}</CardTitle>
        <Select value={selected} onValueChange={setSelected}>
          <SelectTrigger className="w-[220px]">
            <SelectValue placeholder={t('select_alloy', lang)} />
          </SelectTrigger>
          <SelectContent>
            {alloys.map((a) => (
              <SelectItem key={a.id} value={String(a.id)}>
                {a.code}{a.description ? ` — ${a.description}` : ''}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </CardHeader>
      <CardContent>
        {chartData.length === 0 ? (
          <div className="flex h-[280px] items-center justify-center text-sm text-muted-foreground">
            {t('no_data', lang)}
          </div>
        ) : (
          <div className="h-[280px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 8, right: 16, bottom: 0, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" width={56}
                       tickFormatter={(v) => `€${v}`} />
                <RTooltip
                  contentStyle={{ borderRadius: 8, border: '1px solid hsl(var(--border))' }}
                  formatter={(value, name) => [`€${value}/T`, name]}
                />
                <Legend />
                {SERIES.map((s) => (
                  <Line
                    key={s.key}
                    type="monotone"
                    dataKey={s.key}
                    name={t(s.labelKey, lang)}
                    stroke={s.color}
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    activeDot={{ r: 5 }}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
